import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ContentProvider } from '@/types/knowledge';
import { AlertTriangle, Edit, History, Plus, Settings, Trash } from 'lucide-react';
import { useState } from 'react';
import { ContentProviderEditDialog } from './ContentProviderEditDialog';
import { ManageWorkflow } from './ManageWorkflow';
import { RunHistorySheet } from './RunHistorySheet';

// interface ContentProvidersTableProps {
//     knowledgeSetId: string;
// }

// Mock data for content providers
const mockContentProviders: ContentProvider[] = [
    {
        id: '1',
        name: 'SharePoint',
        last_run_date: '2024-01-20T10:30:00Z',
        last_run_status: 'Success',
    },
    {
        id: '2',
        name: 'Confluence',
        last_run_date: '2024-01-19T14:20:00Z',
        last_run_status: 'Failure',
        failure_message: 'Connection timeout. Please check your credentials.',
    },
    {
        id: '3',
        name: 'Google Drive',
        last_run_date: '2024-01-21T09:15:00Z',
        last_run_status: 'Success',
    },
];

export const ContentProvidersTable = () => {
    const [providers, setProviders] = useState<ContentProvider[]>(mockContentProviders);
    const [runHistoryOpen, setRunHistoryOpen] = useState(false);
    const [selectedProviderForHistory, setSelectedProviderForHistory] =
        useState<ContentProvider | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingProvider, setEditingProvider] = useState<ContentProvider | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [providerToDelete, setProviderToDelete] = useState<ContentProvider | null>(null);
    const [workflowOpen, setWorkflowOpen] = useState(false);
    const [selectedProviderForWorkflow, setSelectedProviderForWorkflow] =
        useState<ContentProvider | null>(null);

    const handleEdit = (provider: ContentProvider) => {
        setEditingProvider(provider);
        setEditDialogOpen(true);
    };

    const handleSave = (providerData: Partial<ContentProvider>) => {
        if (editingProvider) {
            setProviders(
                providers.map((p) =>
                    p.id === editingProvider.id
                        ? {
                            ...p,
                            ...providerData,
                        }
                        : p
                )
            );
        }
        setEditDialogOpen(false);
        setEditingProvider(null);
    };

    const handleDelete = (provider: ContentProvider) => {
        setProviderToDelete(provider);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (providerToDelete) {
            setProviders(providers.filter((p) => p.id !== providerToDelete.id));
            setDeleteDialogOpen(false);
            setProviderToDelete(null);
        }
    };

    const handleRunHistory = (provider: ContentProvider) => {
        setSelectedProviderForHistory(provider);
        setRunHistoryOpen(true);
    };

    const handleManageWorkflow = (provider: ContentProvider) => {
        setSelectedProviderForWorkflow(provider);
        setWorkflowOpen(true);
    };

    const handleCreateNew = () => {
        setEditingProvider(null);
        setEditDialogOpen(true);
    };

    return (
        <div className="w-full">
            {/* Header with Create Button */}
            <div className="flex justify-end mb-4">
                <button
                    onClick={handleCreateNew}
                    className="font-medium py-2 px-4 rounded-lg flex items-center transition-colors bg-primary text-primary-foreground hover:bg-primary/90"
                >
                    <Plus size={18} className="mr-2" />
                    Create Content Provider
                </button>
            </div>

            {/* Table */}
            <div
                className="overflow-x-auto rounded-lg shadow bg-background"
            >
                <table
                    className="min-w-full divide-y bg-background"
                >
                    <thead>
                        <tr>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                            >
                                Name
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                            >
                                Last Run Date
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                            >
                                Last Run Status
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {providers.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="px-6 py-8 text-center">
                                    <p className="text-muted-foreground">
                                        No content providers configured yet. Use the "Create Content Provider" button to
                                        create one.
                                    </p>
                                </td>
                            </tr>
                        ) : (
                            providers.map((provider) => (
                                <tr key={provider.id} className="hover:bg-muted/50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm font-medium text-foreground">
                                            {provider.name}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm text-muted-foreground">
                                            {new Date(provider.last_run_date).toLocaleDateString()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <span
                                                        className={`px-2 py-1 inline-flex items-center text-xs font-bold rounded-full ${provider.last_run_status === 'Success'
                                                            ? 'text-secondary bg-secondary/10'
                                                            : 'text-destructive bg-destructive/10'
                                                            }`}
                                                    >
                                                        {provider.last_run_status === 'Failure' && (
                                                            <AlertTriangle size={12} className="mr-1" />
                                                        )}
                                                        {provider.last_run_status}
                                                    </span>
                                                </TooltipTrigger>
                                                {provider.last_run_status === 'Failure' && provider.failure_message && (
                                                    <TooltipContent>
                                                        <p>{provider.failure_message}</p>
                                                    </TooltipContent>
                                                )}
                                            </Tooltip>
                                        </TooltipProvider>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex justify-end items-center gap-2">
                                            <button
                                                onClick={() => handleEdit(provider)}
                                                className="transition-colors p-1 text-muted-foreground hover:text-primary"
                                                title="Edit"
                                            >
                                                <Edit size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(provider)}
                                                className="transition-colors p-1 text-muted-foreground hover:text-destructive"
                                                title="Delete"
                                            >
                                                <Trash size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleRunHistory(provider)}
                                                className="transition-colors p-1 text-muted-foreground hover:text-primary"
                                                title="Run History"
                                            >
                                                <History size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleManageWorkflow(provider)}
                                                className="transition-colors p-1 text-muted-foreground hover:text-primary"
                                                title="Manage Workflow"
                                            >
                                                <Settings size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Edit Dialog */}
            <ContentProviderEditDialog
                isOpen={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setEditingProvider(null);
                }}
                onSave={handleSave}
                contentProvider={editingProvider}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center">
                            <AlertTriangle className="mr-2 text-destructive" size={20} />
                            Confirm Delete
                        </DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete the content provider{' '}
                            <span className="font-semibold">{providerToDelete?.name}</span>? This action cannot be
                            undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <button
                            onClick={() => setDeleteDialogOpen(false)}
                            className="px-4 py-2 border rounded-md transition-colors border-input hover:bg-muted text-foreground"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={confirmDelete}
                            className="px-4 py-2 rounded-md transition-colors bg-destructive text-destructive-foreground hover:opacity-90"
                        >
                            Delete
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Run History Sheet */}
            {selectedProviderForHistory && (
                <RunHistorySheet
                    isOpen={runHistoryOpen}
                    onClose={() => {
                        setRunHistoryOpen(false);
                        setSelectedProviderForHistory(null);
                    }}
                    provider={selectedProviderForHistory}
                />
            )}

            {/* Manage Workflow */}
            {selectedProviderForWorkflow && (
                <ManageWorkflow
                    isOpen={workflowOpen}
                    onClose={() => {
                        setWorkflowOpen(false);
                        setSelectedProviderForWorkflow(null);
                    }}
                    provider={selectedProviderForWorkflow}
                />
            )}
        </div>
    );
};
